import React, { useCallback, useMemo } from 'react';
import noop from 'lodash/noop';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { EditOutline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { CounterPill, LinkButton, Stack, Typography } from '@lyearn/ui';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import AttachedTags from '@/modules/corporate/modules/tags/components/AttachedTags';
import { adaptTagsOnEdit } from '@/modules/corporate/modules/tags/utils';
import { learnerWidgetStyles } from '@/modules/learner/views/LearnerProfile/styles';
import { EntityTagInput, WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import useSeeMoreList from '../../hooks/useSeeMoreList';
import LearnerEditTagsModal from '../LearnerEditTagsModal';
import { linkStyles } from './styles';
import { LearnerTagsWidgetProps } from './types';

function LearnerTagsWidget(props: LearnerTagsWidgetProps) {
  const { t } = useTranslation();
  const { learnerId, tags, onAction } = props;
  const [open, { on, off }] = useToggle(false);
  const adaptedTags = useMemo(() => adaptTagsOnEdit(tags as EntityTagInput[]), [tags]);

  const { shouldShowMore, itemsToShow, toggle, buttonText, endIcon } = useSeeMoreList(
    adaptedTags?.tags || [],
    4,
  );

  const { isAccessible: isWorkspaceAccessible, showPermissionSnackbar } =
    useWorkspaceAccessControl();

  const onEditClick = useCallback(() => {
    isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
      ? on()
      : showPermissionSnackbar('edit', 'learner');
  }, [isWorkspaceAccessible, on, showPermissionSnackbar]);

  return (
    <Stack className={cn(learnerWidgetStyles, 'group')} direction="col">
      <Stack alignItems="center">
        <Typography variant="heading-xs">{t('Common:Form.Tags')}</Typography>
        {adaptedTags?.tags?.length ? (
          <>
            <CounterPill className="ms-8" label={adaptedTags.tags.length || 0} />
            <LinkButton
              className="py-2 px-4 opacity-0 ms-auto group-hover:opacity-100"
              color={'internal'}
              startIcon={<EditOutline />}
              onClick={onEditClick}>
              {t('Common:Actions.Edit')}
            </LinkButton>
          </>
        ) : null}
      </Stack>
      {itemsToShow.length ? (
        <>
          <AttachedTags
            readOnly
            className="mt-8"
            selectedTagValues={adaptedTags?.tagValues}
            tags={itemsToShow}
            onRemoveTag={noop}
            onTagsValueChange={noop}
          />
          {shouldShowMore ? (
            <LinkButton
              className={cn('py-2 px-4', 'mt-16', linkStyles)}
              color={'internal'}
              endIcon={endIcon}
              onClick={toggle}>
              {buttonText}
            </LinkButton>
          ) : null}
        </>
      ) : (
        <AddBlock
          className="mt-20"
          length="short"
          title={t('LearnerProfile:Tags.AddTags')}
          onClick={onEditClick}
        />
      )}
      {open ? (
        <LearnerEditTagsModal learnerId={learnerId} tags={tags} onAction={onAction} onClose={off} />
      ) : null}
    </Stack>
  );
}

export default LearnerTagsWidget;
